import type Phaser from "phaser";

/**
 * One image of the level burst with where and how it was last drawn, so a frame that changes nothing touches nothing
 * (the emoji surface's rule). Every position and size is in device pixels and goes to the image divided by the UI scale,
 * so an edge of the art stands on the device grid at every UI scale.
 */
export interface BurstSprite {
  readonly image: Phaser.GameObjects.Image;
  shown: boolean;
  frame: string;
  tint: number;
  left: number;
  top: number;
  width: number;
  height: number;
  scale: number;
  alpha: number;
}

/**
 * A sprite drawn nowhere yet.
 *
 * @param image Its hidden image, already in the UI layer.
 * @returns The sprite.
 */
export function burstSprite(image: Phaser.GameObjects.Image): BurstSprite {
  return { image, shown: false, frame: "", tint: 0xffffff, left: Number.NaN, top: Number.NaN, width: Number.NaN, height: Number.NaN, scale: 0, alpha: 1 };
}

/**
 * Hides a sprite, if shown.
 *
 * @param sprite The sprite.
 */
export function hideSprite(sprite: BurstSprite): void {
  if (sprite.shown) {
    sprite.image.setVisible(false);
    sprite.shown = false;
  }
}

/**
 * Draws a sprite's frame at a box in device pixels, tinted and at a strength, setting only what changed.
 *
 * @param sprite The sprite.
 * @param frame The atlas frame to show.
 * @param left The box's left in device pixels.
 * @param top The box's top.
 * @param width The box's width.
 * @param height The box's height.
 * @param scale The UI scale.
 * @param tint The tint, 0xffffff for the frame's own colours.
 * @param alpha The strength, 0 to 1.
 */
export function putSprite(sprite: BurstSprite, frame: string, left: number, top: number, width: number, height: number, scale: number, tint: number, alpha: number): void {
  if (sprite.frame !== frame) {
    sprite.image.setFrame(frame);
    sprite.frame = frame;
  }
  if (left !== sprite.left || top !== sprite.top || scale !== sprite.scale) {
    sprite.image.setPosition(left / scale, top / scale);
    sprite.left = left;
    sprite.top = top;
  }
  if (width !== sprite.width || height !== sprite.height || scale !== sprite.scale) {
    sprite.image.setDisplaySize(width / scale, height / scale);
    sprite.width = width;
    sprite.height = height;
  }
  sprite.scale = scale;
  if (tint !== sprite.tint) {
    sprite.image.setTint(tint);
    sprite.tint = tint;
  }
  if (alpha !== sprite.alpha) {
    sprite.image.setAlpha(alpha);
    sprite.alpha = alpha;
  }
  if (!sprite.shown) {
    sprite.image.setVisible(true);
    sprite.shown = true;
  }
}
